import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import { AlertTriangle } from 'lucide-react';
import { Button } from '@scio3/ui';
import { cn } from '@/lib/utils';
import { upcomingShutoffText } from '@/lib/dunning-shutoff';
import { useDunningState } from '@/hooks/useDunningState';
import { useDunningContactLine } from '@/hooks/useDunningContactLine';

/**
 * Persistent in-portal notice for a past-due account.
 *
 * Three research findings shape this and are easy to undo by accident:
 *
 * 1. **It must not look like our promotional banner.** Banner blindness is the
 *    documented failure mode — a billing notice styled like marketing gets ignored
 *    exactly the way marketing does. Hence `variant="destructive"`/`warning` from
 *    the shared `Alert`, plain text, real numbers, no illustration, no gradient.
 *    `AppShell` also suppresses `PromotionalBanner` while this is up, so the two
 *    never stack.
 * 2. **It states a dated consequence.** "Your screens stop playing on 24 August
 *    2026", not "your account needs attention". The date is the single strongest
 *    lever in the evidence; vague urgency measurably underperforms.
 * 3. **One click to pay, and nothing else.** Most delinquency is accidental, so the
 *    win is removing steps. There is exactly one CTA and it goes straight to the
 *    payment page.
 *
 * `role="status"` rather than `role="alert"`: this is persistent page furniture, so
 * an assertive live region would re-interrupt a screen-reader user on every route
 * change. There is no dismiss control — the notice ends when the debt does.
 */
export function DunningBanner() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { showBanner, dunning, isLimited, payPath, canPay } = useDunningState();
  const contactLine = useDunningContactLine();

  const handlePay = useCallback(() => {
    navigate(payPath);
  }, [navigate, payPath]);

  if (!showBanner || !dunning) return null;

  // Only a date still ahead; a passed one falls back to the undated copy.
  const shutoffText = upcomingShutoffText(dunning.shutoffAt);

  /**
   * Two variants, not five.
   *
   * The projection says whether changes are blocked and quotes one date; it no longer
   * says whether the account is card- or invoice-billed, so the copy names no payment
   * method and the CTA goes to the invoice list, which serves both. `suspended` has no
   * variant either: such an account has `payment.isActive: false` and meets the
   * inactive-account dialog instead of ever rendering this bar.
   */
  const message = isLimited
    ? t('dunning.banner.limited')
    : shutoffText
      ? t('dunning.banner.dated', { date: shutoffText })
      : t('dunning.banner.undated');

  /**
   * Full-bleed bar matching PromotionalBanner's geometry, in a MUTED pastel — orange
   * for past due, red once playback is stopped / writes are blocked. Design review
   * (2026-10) rejected the earlier saturated orange/red as too loud for a billing
   * notice. It shares the DunningAdvisoryBanner's soft-tint treatment so the
   * family reads as one system. The tint carries the severity on its own: no
   * border, and the text is near-black (white in dark mode) for maximum contrast rather
   * than a tinted shade. Only the icon is coloured. The colour family, not
   * loudness, separates it from the amber advisory. The one detail a customer
   * scans for, the date, stays bold.
   */
  // Semantic tokens (globals.css `--color-dunning-*`), light and dark defined there.
  const tone = isLimited ? 'bg-dunning-limited' : 'bg-dunning-past-due';
  const iconTone = isLimited ? 'text-dunning-limited-icon' : 'text-dunning-past-due-icon';

  // Bold the shutoff date within the message, without splitting the translated
  // string into fragments — find the interpolated date and wrap just that.
  const messageNode =
    shutoffText && message.includes(shutoffText)
      ? (() => {
          const i = message.indexOf(shutoffText);
          return (
            <>
              {message.slice(0, i)}
              <strong className="font-bold">{shutoffText}</strong>
              {message.slice(i + shutoffText.length)}
            </>
          );
        })()
      : message;

  return (
    <div
      className={cn(
        'flex min-h-12 w-full items-center justify-center gap-4 px-6 py-2 text-sm text-dunning-foreground',
        tone,
      )}
      role="status"
      data-testid="dunning-banner"
    >
      {/* One line only. The reassurance lives in the login popup, where there is
          room — a banner past one line stops being scannable, which is the whole job
          of a banner. The contact line appears here only in place of the button. */}
      <span className="flex items-center gap-2">
        <AlertTriangle className={cn('h-5 w-5 shrink-0', iconTone)} aria-hidden="true" />
        <span>{messageNode}</span>
      </span>
      {/* THE action: the solid (default) Button variant in the dunning action tokens
          rather than primary teal, so it reads as one near-black CTA on both tints in
          both themes. A viewer who cannot open billing gets the contact
          line in its place instead of a button that lands on "no access"
          (see canOpenPayPath). */}
      {canPay ? (
        <Button
          size="sm"
          onClick={handlePay}
          data-testid="dunning-banner-pay-btn"
          className="shrink-0 rounded-md bg-dunning-action px-5 font-semibold text-dunning-action-foreground transition-colors hover:bg-dunning-action-hover"
        >
          {t('dunning.cta.viewInvoice')}
        </Button>
      ) : (
        contactLine && <span data-testid="dunning-banner-contact-line">{contactLine}</span>
      )}
    </div>
  );
}
